// File: frontend/src/pages/Pipeline/PipelinePage.jsx
// Pipeline Kanban - Vista principal del pipeline de vendas com drag & drop

import React, { useState, useCallback, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { DragDropContext } from 'react-beautiful-dnd';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import toast from 'react-hot-toast';
import OpportunityForm from '../../components/Opportunities/OpportunityForm';
import PipelineHeader from '../../components/Pipeline/PipelineHeader';
import PipelineColumn from '../../components/Pipeline/PipelineColumn';
import {
  ExclamationTriangleIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';

const PipelinePage = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState('');
  const [periodFilter, setPeriodFilter] = useState('');
  const [valueRange, setValueRange] = useState({ min: '', max: '' });
  const [selectedStage, setSelectedStage] = useState('');
  const [selectedSource, setSelectedSource] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  
  // Form state
  const [showForm, setShowForm] = useState(false);
  const [editingOpportunity, setEditingOpportunity] = useState(null);

  // Real-time state
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  
  // Stage definitions com cores específicas para o Kanban
  const stages = [
    { key: 'lead', label: 'Lead' },
    { key: 'qualified', label: 'Qualificado' },
    { key: 'proposal', label: 'Proposta' },
    { key: 'negotiation', label: 'Negociação' },
    { key: 'closed_won', label: 'Fechou Ganho' },
    { key: 'closed_lost', label: 'Fechou Perdido' }
  ];

  // Sources for filter
  const sources = [
    'Website',
    'Google Ads',
    'Facebook Ads',
    'LinkedIn',
    'Cold Call',
    'Referência',
    'Email Marketing',
    'Evento',
    'Partner',
    'Outro'
  ];

  // Update online status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Fetch opportunities for pipeline
  const { data: opportunitiesData, isLoading, error, refetch } = useQuery(
    ['pipeline-opportunities', { 
      search: searchTerm, 
      user: selectedUser, 
      period: periodFilter, 
      minValue: valueRange.min,
      maxValue: valueRange.max,
      stage: selectedStage,
      source: selectedSource
    }],
    async () => {
      const params = new URLSearchParams({
        limit: '1000' // Get all opportunities for pipeline view
      });
      
      if (searchTerm) params.append('search', searchTerm);
      if (selectedUser) params.append('user_id', selectedUser);
      if (selectedStage) params.append('stage', selectedStage);
      if (selectedSource) params.append('source', selectedSource);
      if (valueRange.min) params.append('value_min', valueRange.min);
      if (valueRange.max) params.append('value_max', valueRange.max);
      
      // Handle period filtering
      if (periodFilter) {
        params.append('date_range', periodFilter);
      }
      
      const response = await api.get(`/opportunities?${params}`);
      setLastUpdate(new Date());
      return response.data;
    },
    {
      enabled: !!user?.id,
      refetchInterval: 30000, // Refresh every 30 seconds
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
    }
  );

  // Fetch pipeline stats with same filters
  const { data: statsData, error: statsError, isLoading: statsLoading } = useQuery(
    ['pipeline-stats', { 
      user: selectedUser, 
      period: periodFilter 
    }],
    async () => {
      const params = new URLSearchParams();

      if (selectedUser) params.append('user_id', selectedUser);
      if (periodFilter) params.append('date_range', periodFilter);

      const response = await api.get(`/opportunities/stats/dashboard?${params}`);
      return response.data;
    },
    {
      enabled: !!user?.id,
      refetchInterval: 30000,
      retry: 3,
      retryDelay: 1000,
      // No tragar el error devolviendo ceros: el header ya tolera statsData
      // undefined, y el usuario debe enterarse de que las métricas no cargaron.
      onError: () => {
        toast.error('Não foi possível carregar as métricas do pipeline', { id: 'pipeline-stats-error' });
      }
    }
  );

  // Fetch users for filter
  const { data: usersData } = useQuery(
    'users-list',
    async () => {
      const response = await api.get('/users?limit=100');
      return response.data.users || [];
    },
    { enabled: !!user?.id }
  );

  // Update stage mutation with optimistic updates
  const updateStageMutation = useMutation(
    ({ id, stage }) => api.put(`/opportunities/${id}`, { stage }),
    {
      onMutate: async ({ id, stage }) => {
        // Cancel any outgoing refetches
        await queryClient.cancelQueries('pipeline-opportunities');
        
        // Snapshot the previous value
        const previousData = queryClient.getQueryData('pipeline-opportunities');
        
        // Optimistically update to the new value
        queryClient.setQueryData('pipeline-opportunities', (old) => {
          if (!old?.data) return old;
          
          return {
            ...old,
            data: old.data.map(opp => 
              opp.id === id ? { ...opp, stage, updated_at: new Date().toISOString() } : opp
            )
          };
        });
        
        return { previousData };
      },
      onError: (err, variables, context) => {
        // Rollback on error
        if (context?.previousData) {
          queryClient.setQueryData('pipeline-opportunities', context.previousData);
        }
        toast.error(err.response?.data?.message || 'Erro ao atualizar stage');
      },
      onSuccess: () => {
        // Invalidate and refetch
        queryClient.invalidateQueries('pipeline-opportunities');
        queryClient.invalidateQueries('pipeline-stats');
        toast.success('Etapa atualizada com sucesso!');
        setLastUpdate(new Date());
      },
    }
  );

  // Organize opportunities by stage.
  // Matching is case-insensitive and trims whitespace so prod values like
  // "Proposal" / " closed_won " still land in the right column. Any opportunity
  // whose stage doesn't match a known column is dropped into the first column
  // (instead of vanishing) so no card is ever lost from the board.
  const opportunitiesByStage = React.useMemo(() => {
    const opportunities = opportunitiesData?.data || [];
    const organized = {};

    const stageKeys = new Set(stages.map(s => s.key));
    stages.forEach(stage => {
      organized[stage.key] = [];
    });

    const normalize = (value) => (value ?? '').toString().trim().toLowerCase();

    opportunities.forEach(opp => {
      const key = normalize(opp.stage);
      if (stageKeys.has(key)) {
        organized[key].push(opp);
      } else {
        // Unknown/empty stage: keep it visible in the first column instead of losing it.
        organized[stages[0].key].push(opp);
      }
    });

    return organized;
  }, [opportunitiesData?.data]);

  // Calculate stage metrics.
  // opp.value puede llegar como string (NUMERIC de Postgres) — siempre Number().
  // La antigua "taxa de conversão" por columna (count actual / count anterior en el
  // snapshot) producía valores absurdos (300%, 120%) — se reemplazó por valor
  // ponderado por probabilidade, que sí es interpretable para el equipo comercial.
  const getStageMetrics = useCallback((stageKey) => {
    const opportunities = opportunitiesByStage[stageKey] || [];
    const count = opportunities.length;
    const totalValue = opportunities.reduce((sum, opp) => sum + (Number(opp.value) || 0), 0);
    const avgValue = count > 0 ? totalValue / count : 0;

    return { count, totalValue, avgValue };
  }, [opportunitiesByStage]);

  // Handle drag end
  const handleDragEnd = useCallback((result) => {
    const { destination, source, draggableId } = result;

    // Dropped outside the list
    if (!destination) return;

    // Dropped in the same position
    if (destination.droppableId === source.droppableId && destination.index === source.index) {
      return;
    }

    // IMPORTANT: opportunity ids are UUIDs (varchar) — never parseInt() them,
    // doing so corrupts the id and the PUT silently targets the wrong/no row.
    const opportunityId = draggableId;
    const newStage = destination.droppableId;

    // Update the stage (droppableId is already a valid backend stage slug)
    updateStageMutation.mutate({ id: opportunityId, stage: newStage });
  }, [updateStageMutation]);

  // Handle form actions
  const handleEditOpportunity = (opportunity) => {
    setEditingOpportunity(opportunity);
    setShowForm(true);
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingOpportunity(null);
    queryClient.invalidateQueries('pipeline-opportunities');
    queryClient.invalidateQueries('pipeline-stats');
    setLastUpdate(new Date());
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingOpportunity(null);
  };

  const handleCreateNew = (stage = 'lead') => {
    setEditingOpportunity({ stage });
    setShowForm(true);
  };

  const handleRefresh = () => {
    refetch();
    toast.success('Pipeline atualizado');
  };

  // Clear all filters
  const clearFilters = () => {
    setSearchTerm('');
    setSelectedUser('');
    setPeriodFilter('');
    setValueRange({ min: '', max: '' });
    setSelectedStage('');
    setSelectedSource('');
  };

  if (isLoading && !opportunitiesData) {
    return (
      <div className="p-6 max-w-full mx-auto">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-64 mb-6"></div>
          <div className="grid grid-cols-6 gap-4">
            {stages.map((stage, index) => (
              <div key={index} className="space-y-4">
                <div className="h-6 bg-gray-200 rounded"></div>
                <div className="space-y-2">
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="h-24 bg-gray-100 rounded"></div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 max-w-full mx-auto">
        <div className="text-center">
          <ExclamationTriangleIcon className="h-12 w-12 text-red-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
            Erro ao carregar pipeline
          </h3>
          <p className="text-gray-600 dark:text-gray-300 mb-4">
            {error.response?.data?.message || 'Tente novamente mais tarde'}
          </p>
          <button
            onClick={() => refetch()}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center space-x-2 mx-auto"
          >
            <ArrowPathIcon className="h-5 w-5" />
            <span>Tentar Novamente</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-full mx-auto">
      {/* Enhanced Pipeline Header */}
      <PipelineHeader
        statsData={statsData}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        selectedUser={selectedUser}
        setSelectedUser={setSelectedUser}
        periodFilter={periodFilter}
        setPeriodFilter={setPeriodFilter}
        valueRange={valueRange}
        setValueRange={setValueRange}
        selectedStage={selectedStage}
        setSelectedStage={setSelectedStage}
        selectedSource={selectedSource}
        setSelectedSource={setSelectedSource}
        showFilters={showFilters}
        setShowFilters={setShowFilters}
        users={usersData}
        stages={stages}
        sources={sources}
        onRefresh={handleRefresh}
        onCreateNew={() => handleCreateNew()}
        isOnline={isOnline}
        lastUpdate={lastUpdate}
      />

      {/* Kanban Board */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mt-6">
        <DragDropContext onDragEnd={handleDragEnd}>
          <div className="overflow-x-auto">
            <div className="flex gap-6 min-h-[600px] pb-4" style={{ minWidth: 'max-content' }}>
              {stages.map((stage) => {
                const metrics = getStageMetrics(stage.key);
                const opportunities = opportunitiesByStage[stage.key] || [];
                
                return (
                  <PipelineColumn
                    key={stage.key}
                    stage={stage}
                    opportunities={opportunities}
                    metrics={metrics}
                    onCreateNew={() => handleCreateNew(stage.key)}
                    onEditOpportunity={handleEditOpportunity}
                  />
                );
              })}
            </div>
          </div>
        </DragDropContext>
      </div>

      {/* Opportunity Form Modal */}
      <OpportunityForm
        isOpen={showForm}
        onClose={handleCloseForm}
        opportunity={editingOpportunity}
        onSuccess={handleFormSuccess}
      />
      
    </div>
  );
};

export default PipelinePage;